import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/dropdown-arrow.svg';

export type DropdownOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

type OptionDropdownProps = {
    options: DropdownOption[];
    value?: string | null;
    onChange: (value: string) => void;
    placeholder?: string;
    width?: number;
    alignLeft?: boolean;
};

const tableFontStyle: React.CSSProperties = {
    fontFamily: 'Assistant, sans-serif',
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    color: '#282828',
};

const OptionDropdown = ({
    options,
    value,
    onChange,
    placeholder = '--',
    width = 150,
    alignLeft = false,
}: OptionDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const selectedOption = useMemo(
        () => options.find((option) => option.value === value) ?? null,
        [options, value]
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleDropdown = () => {
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
            setOpen(true);
            return;
        }

        setOpen(false);
    };

    const dropdown =
        open && position
            ? ReactDOM.createPortal(
                  <div
                      style={{
                          position: 'absolute',
                          top: `${position.top}px`,
                          left: `${position.left}px`,
                          width: `${width}px`,
                          background: '#ffffff',
                          border: '1px solid #d1fae5',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
                          zIndex: 1000,
                          maxHeight: '180px',
                          overflowY: 'auto',
                          ...tableFontStyle,
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                  >
                      {options.length > 0 ? (
                          options.map((option) => (
                              <div
                                  key={option.value}
                                  onClick={() => {
                                      if (option.disabled) {
                                          return;
                                      }
                                      setOpen(false);
                                      onChange(option.value);
                                  }}
                                  style={{
                                      ...tableFontStyle,
                                      padding: '6px 8px',
                                      cursor: option.disabled ? 'not-allowed' : 'pointer',
                                      color: option.disabled ? '#9ca3af' : '#282828',
                                      backgroundColor: option.disabled ? '#f9fafb' : '#ffffff',
                                  }}
                              >
                                  {option.label}
                              </div>
                          ))
                      ) : (
                          <div style={{ padding: '6px 8px', ...tableFontStyle }}>{placeholder}</div>
                      )}
                  </div>,
                  document.body
              )
            : null;

    return (
        <div
            style={{
                display: 'inline-block',
                position: 'relative',
                width: `${width}px`,
                overflow: 'hidden',
            }}
        >
            <button
                ref={buttonRef}
                onClick={handleToggleDropdown}
                type="button"
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    border: 'none',
                    background: 'transparent',
                    padding: '0',
                    ...tableFontStyle,
                }}
            >
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flexShrink: 1,
                        flex: 1,
                        textAlign: alignLeft ? 'left' : 'center',
                        margin: alignLeft || selectedOption ? 'unset' : 'auto',
                    }}
                >
                    {selectedOption?.label || placeholder}
                </span>
                {options.length > 0 && (
                    <ArrowDownIcon
                        fill="#282828"
                        style={{
                            width: '12px',
                            height: '12px',
                            transform: open ? 'rotate(180deg)' : 'rotate(0)',
                            transition: '0.2s ease',
                            flexShrink: 0,
                        }}
                    />
                )}
            </button>
            {dropdown}
        </div>
    );
};

export default OptionDropdown;
